import React, { FC, Fragment, useState } from 'react';
import { Button, Chip, Grid, IconButton, makeStyles, Theme, Tooltip, Typography, MenuItem, TextField } from '@material-ui/core';
import { PopperPlacementType } from '@material-ui/core/Popper';
import { format } from 'date-fns';

import CalendarIcon from '@material-ui/icons/EventNote';
import DeleteIcon from '@material-ui/icons/Cancel';

import PositionedPopper from 'components/PositionedPopper';
import FilterTable from 'components/FilterTable';
import SearchInput from 'components/SearchInput';
import theme from 'theme';

interface Props {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  serviceAddressFilter: ColumnFilter[];
  setServiceAddressFilter: React.Dispatch<React.SetStateAction<ColumnFilter[]>>;
  equipmentFilter: ColumnFilter[];
  setEquipmentFilter: React.Dispatch<React.SetStateAction<ColumnFilter[]>>;
  technicianFilter: ColumnFilter[];
  setTechnicianFilter: React.Dispatch<React.SetStateAction<ColumnFilter[]>>;
  startDate: Date | null;
  setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
  endDate: Date | null;
  setEndDate: React.Dispatch<React.SetStateAction<Date | null>>;
  sortBy: string;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  serviceAddresses: Select[];
  technicians: Select[];
  equipments: Select[];
  filterBy: string;
  setFilterBy: React.Dispatch<React.SetStateAction<string>>;
}

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    fontSize: 20
  },
  deleteIcon: {
    color: '#53A0BE'
  },
  calendarIcon: (props: Props) => ({
    fontSize: 20,
    color: props.filterBy ? theme.palette.primary.main : ''
  }),
  filterChip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  toolbarContainer: {
    padding: theme.spacing(2),
    paddingBottom: 0
  }
}));

const ToolBar: FC<Props> = props => {
  const classes = useStyles(props);
  const [openCalendarPopper, setOpenCalendarPopper] = useState(false);
  const [anchorElCalendarPopper, setAnchorElCalendarPopper] = useState<HTMLElement | null>(null);
  const [placementCalendarPopper, setPlacementCalendarPopper] = useState<PopperPlacementType>();

  const {
    searchText,
    setSearchText,
    serviceAddressFilter,
    setServiceAddressFilter,
    equipmentFilter,
    setEquipmentFilter,
    technicianFilter,
    setTechnicianFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    sortBy,
    setSortBy,
    serviceAddresses,
    technicians,
    equipments,
    filterBy,
    setFilterBy
  } = props;

  const [selectedServiceAddressData, setSelectedServiceAddressData] = useState<Select[]>([]);
  const [selectedEquipmentData, setSelectedEquipmentData] = useState<Select[]>([]);
  const [selectedTechnicianData, setSelectedTechnicianData] = useState<Select[]>([]);

  const handleCalendarFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenCalendarPopper(!openCalendarPopper);
    setAnchorElCalendarPopper(event.currentTarget);
    setPlacementCalendarPopper('bottom-end');
  };

  const handleDeleteServiceAddressFilter = (index: number) => {
    const currentFilter = [...serviceAddressFilter];
    const currentSelectedData = [...selectedServiceAddressData];
    currentFilter.splice(index, 1);
    currentSelectedData.splice(index, 1);

    setServiceAddressFilter(currentFilter);
    setSelectedServiceAddressData(currentSelectedData);
  };

  const handleDeleteEquipmentFilter = (index: number) => {
    const currentFilter = [...equipmentFilter];
    const currentSelectedData = [...selectedEquipmentData];
    currentFilter.splice(index, 1);
    currentSelectedData.splice(index, 1);

    setEquipmentFilter(currentFilter);
    setSelectedEquipmentData(currentSelectedData);
  };

  const handleDeleteTechnicianFilter = (index: number) => {
    const currentFilter = [...technicianFilter];
    const currentSelectedData = [...selectedTechnicianData];
    currentFilter.splice(index, 1);
    currentSelectedData.splice(index, 1);

    setTechnicianFilter(currentFilter);
    setSelectedTechnicianData(currentSelectedData);
  };

  const handleClearFilters = () => {
    setServiceAddressFilter([]);
    setEquipmentFilter([]);
    setTechnicianFilter([]);
    setSelectedServiceAddressData([]);
    setSelectedEquipmentData([]);
    setSelectedTechnicianData([]);
  };

  const renderHeaderLabel = () => {
    if (filterBy) {
      if (filterBy === '5' && startDate && endDate) {
        return (
          <Typography variant='h6'>
            (by date {format(new Date(startDate), 'dd-MM-yyyy')} - {format(new Date(endDate), 'dd-MM-yyyy')})
          </Typography>
        );
      }
    }
  };

  const renderLeftHeader = () => {
    return (
      <div style={{ marginTop: theme.spacing(1) }}>
        <SearchInput
          withBorder
          withTransition={false}
          width={200}
          placeHolder='Search within notes'
          iconColor='#989898'
          tableSearchValue={searchText}
          setTableSearchValue={setSearchText}
        />
      </div>
    );
  };

  const renderRightHeader = () => {
    return (
      <Fragment>
        <Grid container alignItems='flex-start'>
          <Grid container item xs={11}>
            <Grid container justify='flex-end'>
              <Grid item xs={12} container justify='flex-end' spacing={1}>
                <Grid item>
                  <FilterTable
                    masterData={serviceAddresses}
                    selectedData={selectedServiceAddressData}
                    setSelectedData={setSelectedServiceAddressData}
                    columnFilter={serviceAddressFilter}
                    setColumnFilter={setServiceAddressFilter}
                    label='Filter Service Address'
                  />
                </Grid>
                <Grid item>
                  <FilterTable
                    masterData={equipments}
                    selectedData={selectedEquipmentData}
                    setSelectedData={setSelectedEquipmentData}
                    columnFilter={equipmentFilter}
                    setColumnFilter={setEquipmentFilter}
                    label='Filter Equipment'
                  />
                </Grid>
                <Grid item>
                  <FilterTable
                    masterData={technicians}
                    selectedData={selectedTechnicianData}
                    setSelectedData={setSelectedTechnicianData}
                    columnFilter={technicianFilter}
                    setColumnFilter={setTechnicianFilter}
                    label='Filter Technician'
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container justify='flex-end' spacing={1}>
                <Grid item>
                  <TextField
                    variant='outlined'
                    select
                    label='Sort by'
                    size='small'
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    style={{ minWidth: 180, marginRight: theme.spacing(1), marginTop: theme.spacing(1) }}
                  >
                    <MenuItem value='newest'>Newest First</MenuItem>
                    <MenuItem value='oldest'>Oldest First</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container item xs={1} justify='center' style={{ marginTop: theme.spacing(0.5) }}>
            <PositionedPopper
              openPopper={openCalendarPopper}
              setOpenPopper={setOpenCalendarPopper}
              anchorEl={anchorElCalendarPopper}
              placement={placementCalendarPopper}
              containerWidth={320}
              fadeTransition={350}
              popperComponent='dateRangePicker'
              options={[{ key: '5', label: 'Custom Date' }]}
              filterBy={filterBy}
              setFilterBy={setFilterBy}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
            <Tooltip title='Calendar filter' placement='top'>
              <IconButton onClick={event => handleCalendarFilterClick(event)}>
                <CalendarIcon className={classes.calendarIcon} />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid container justify='flex-end'>
          <Grid item xs={12} container justify='flex-end' spacing={1}>
            <Grid item></Grid>
          </Grid>
        </Grid>
      </Fragment>
    );
  };

  const renderFilterHeader = () => {
    if (serviceAddressFilter.length > 0 || equipmentFilter.length > 0 || technicianFilter.length > 0) {
      return (
        <Grid container alignItems='center' spacing={2}>
          <Grid item xs={1}>
            <Button color='primary' onClick={handleClearFilters}>
              <Typography variant='body1'>Clear Filters</Typography>
            </Button>
          </Grid>
          <Grid item xs={11}>
            {serviceAddressFilter.map((value, index) => (
              <Chip
                key={`sa-${index}`}
                label={value.columnName}
                deleteIcon={
                  <Tooltip title='Delete'>
                    <DeleteIcon className={classes.deleteIcon} />
                  </Tooltip>
                }
                className={classes.filterChip}
                onDelete={() => handleDeleteServiceAddressFilter(index)}
              />
            ))}
            {equipmentFilter.map((value, index) => (
              <Chip
                key={`eq-${index}`}
                label={value.columnName}
                deleteIcon={
                  <Tooltip title='Delete'>
                    <DeleteIcon className={classes.deleteIcon} />
                  </Tooltip>
                }
                className={classes.filterChip}
                onDelete={() => handleDeleteEquipmentFilter(index)}
              />
            ))}
            {technicianFilter.map((value, index) => (
              <Chip
                key={`tech-${index}`}
                label={value.columnName}
                deleteIcon={
                  <Tooltip title='Delete'>
                    <DeleteIcon className={classes.deleteIcon} />
                  </Tooltip>
                }
                className={classes.filterChip}
                onDelete={() => handleDeleteTechnicianFilter(index)}
              />
            ))}
          </Grid>
        </Grid>
      );
    }
  };

  return (
    <Grid container spacing={2} className={classes.toolbarContainer}>
      <Grid item xs={5} container justify='flex-start'>
        {renderLeftHeader()}
      </Grid>
      <Grid item xs={7} container justify='flex-end' alignItems='flex-start'>
        {renderRightHeader()}
      </Grid>
      <Grid item xs={12}>
        {renderHeaderLabel()}
      </Grid>
      <Grid item xs={12} container justify='flex-start' alignItems='center'>
        {renderFilterHeader()}
      </Grid>
    </Grid>
  );
};

export default ToolBar;
